// src/pages/.../ConvocationsPage.jsx
import React, { useState, useEffect, useMemo } from "react";
import { useUser } from "./../../contexts/UserContext";
import { getRoleFromRoleId } from "./../../utils/roleHelper";
import useScrollToTop from "./../../hooks/useScrollToTop";
import ScrollIndicator from "./../../hooks/ScrollIndicator";
import { getActeursByUser } from "./../../services/userServices";
import { getAllEventsByActorId } from "./../../services/acteureventServices";
import EventDetailsModal from "./../../pages/CalendarPage/EventDetailsModal";
import ObservationsModal from "./ObservationsModal";
import InfoBubble from "./../../components/InfoBubble/InfoBubble";
import PageHeader from "./../../components/Layout/PageHeader";
import { FaInfoCircle } from "react-icons/fa";

const ConvocationsPage = () => {
  useScrollToTop();
  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isObservationsModalOpen, setIsObservationsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedObservations, setSelectedObservations] = useState(null);

  // Filtres
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedPoste, setSelectedPoste] = useState("");
  const [showOnlyFuture, setShowOnlyFuture] = useState(true);

  useEffect(() => {
    const fetchEvents = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        const acteursResponse = await getActeursByUser(userId);

        // Récupère les convocations pour chaque acteur en parallèle
        const eventsArrays = await Promise.all(
          (acteursResponse || []).map(async (acteur) => {
            const eventsResponse = await getAllEventsByActorId(acteur.id);
            return eventsResponse?.Events || [];
          })
        );

        const allEvents = eventsArrays.flat();

        // On trie par date (les plus récentes / à venir d’abord)
        allEvents.sort((a, b) => {
          const da = new Date(a.start);
          const db = new Date(b.start);
          const va = isNaN(da) ? 0 : da.getTime();
          const vb = isNaN(db) ? 0 : db.getTime();
          return va - vb; // tri croissant (les plus anciennes d’abord)
        });

        setEvents(allEvents);
      } catch (error) {
        console.error("Erreur lors du chargement des événements", error);
      } finally {
        setLoading(false);
      }
    };

    fetchEvents();
  }, [userId]);

  const typeOptions = useMemo(() => {
    const s = new Set();
    events.forEach((e) => e.type_event && s.add(e.type_event));
    return Array.from(s).sort();
  }, [events]);

  const posteOptions = useMemo(() => {
    const s = new Set();
    events.forEach((e) => e.ActeurEvent?.poste && s.add(e.ActeurEvent.poste));
    return Array.from(s).sort();
  }, [events]);

  const filteredEvents = useMemo(() => {
    const now = new Date();
    let list = [...events];

    // Filtre futur / passé
    if (showOnlyFuture) {
      list = list.filter((e) => {
        const d = new Date(e.start);
        if (isNaN(d)) return true;
        return d >= now;
      });
    }

    // Filtre type
    if (selectedType) {
      list = list.filter((e) => e.type_event === selectedType);
    }

    // Filtre poste
    if (selectedPoste) {
      list = list.filter((e) => e.ActeurEvent?.poste === selectedPoste);
    }

    // Recherche texte : titre + type + poste
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter((e) => {
        const titre = (e.titre || "").toLowerCase();
        const type = (e.type_event || "").toLowerCase();
        const poste = (e.ActeurEvent?.poste || "").toLowerCase();
        return (
          titre.includes(q) ||
          type.includes(q) ||
          (poste && poste.includes(q))
        );
      });
    }

    // Re-tri par date croissante (les plus proches en haut)
    list.sort((a, b) => {
      const da = new Date(a.start);
      const db = new Date(b.start);
      const va = isNaN(da) ? 0 : da.getTime();
      const vb = isNaN(db) ? 0 : db.getTime();
      return va - vb;
    });

    return list;
  }, [events, showOnlyFuture, selectedType, selectedPoste, searchQuery]);

  if (!userId) {
    return (
      <div className="mt-40 text-center text-slate-600 dark:text-slate-200">
        Chargement de l’utilisateur…
      </div>
    );
  }

  const openEventModal = (event) => {
    setSelectedEvent(event);
    setIsEventModalOpen(true);
  };

  const closeEventModal = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
  };

  const openObservationsModal = (observations) => {
    setSelectedObservations(observations);
    setIsObservationsModalOpen(true);
  };

  const closeObservationsModal = () => {
    setIsObservationsModalOpen(false);
    setSelectedObservations(null);
  };

  return (
    <div className="flex flex-col w-full mx-auto min-h-screen mt-[130px] sm:mt-[130px] md:mt-[150px] lg:mt-[150px] xl:mt-[150px] px-2 pb-10 bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-50">
      <ScrollIndicator />

      <PageHeader
        eyebrow="Acteurs du Judo"
        title="Convocations départementales"
        subtitle="Retrouvez ici toutes vos convocations sur les compétitions et animations départementales : type d’événement, poste, notes et éventuelles observations des responsables de l’arbitrage."
        align="center"
        size="md"
      />

      <div className="w-full max-w-6xl mx-auto space-y-4">
        <InfoBubble title="Vos convocations">
          <p>
            Les convocations affichées sont liées aux rôles que vous occupez
            (responsable/arbitre/CS/bénévolontaire) sur les différents événements du calendrier.
            Utilisez les filtres pour retrouver rapidement une compétition ou un
            poste spécifique.
          </p>
        </InfoBubble>

        {/* Filtres */}
        <div className="rounded-xl bg-white/90 dark:bg-slate-900/90 border border-slate-200/70 dark:border-slate-700/70 shadow-sm p-3 sm:p-4 space-y-3">
          <div className="flex flex-col md:flex-row gap-3">
            {/* Recherche globale */}
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Rechercher
              </label>
              <input
                type="text"
                placeholder="Titre, type, poste..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              />
            </div>

            {/* Type d’événement */}
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Type d’événement
              </label>
              <select
                value={selectedType}
                onChange={(e) => setSelectedType(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <option value="">Tous les types</option>
                {typeOptions.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>

            {/* Poste */}
            <div className="flex-1">
              <label className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400 mb-1">
                Poste
              </label>
              <select
                value={selectedPoste}
                onChange={(e) => setSelectedPoste(e.target.value)}
                className="w-full rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/70"
              >
                <option value="">Tous les postes</option>
                {posteOptions.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Ligne des toggles */}
          <div className="flex flex-col sm:flex-row sm:items-center gap-2">
            <button
              type="button"
              onClick={() => setShowOnlyFuture((v) => !v)}
              className={`w-full sm:w-auto text-xs sm:text-[0.8rem] inline-flex items-center justify-center rounded-full border px-3 py-1.5 ${
                showOnlyFuture
                  ? "bg-primary text-white border-primary"
                  : "border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200"
              }`}
            >
              {showOnlyFuture
                ? "Uniquement convocations à venir"
                : "Inclure les convocations passées"}
            </button>

            <p className="text-[0.7rem] sm:text-xs text-slate-500 dark:text-slate-400">
              Rôle actuel : <span className="font-semibold">{userRole}</span>
            </p>
          </div>
        </div>

        {/* Tableau des convocations */}
        <div className="overflow-x-auto rounded-xl shadow-sm border border-slate-200/80 dark:border-slate-800/80 bg-white/90 dark:bg-slate-900/90">
          {loading ? (
            <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-300">
              Chargement des événements…
            </div>
          ) : filteredEvents.length === 0 ? (
            <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-300">
              Aucune convocation ne correspond à vos critères.
            </div>
          ) : (
            <table className="min-w-full border-collapse text-xs sm:text-sm font-montserrat">
              <thead className="bg-slate-900 text-white">
                <tr>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Titre
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Poste
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Note
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Observations
                  </th>
                  <th className="px-3 sm:px-4 py-3 border-b border-slate-700 text-left text-[0.7rem] font-semibold uppercase tracking-wider">
                    Détails
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => {
                  const d = new Date(event.start);
                  const hasObs = !!event.ActeurEvent?.observations;

                  return (
                    <tr
                      key={event.id}
                      className="border-b border-slate-200 dark:border-slate-800 hover:bg-slate-50/80 dark:hover:bg-slate-800/60 transition-colors"
                    >
                      <td className="px-3 sm:px-4 py-2 align-top">
                        {!isNaN(d)
                          ? d.toLocaleDateString("fr-FR", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                            })
                          : "-"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top font-semibold">
                        {event.titre}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top">
                        {event.type_event}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top">
                        {event.ActeurEvent?.poste || "-"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top">
                        {event.ActeurEvent?.note ?? "—"}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top">
                        {hasObs ? (
                          <button
                            onClick={() =>
                              openObservationsModal(
                                event.ActeurEvent.observations
                              )
                            }
                            className="inline-flex items-center text-xs sm:text-[0.8rem] text-primary hover:text-primary/80 underline underline-offset-2"
                          >
                            Voir
                          </button>
                        ) : (
                          <span className="text-[0.75rem] text-slate-400">
                            Aucune
                          </span>
                        )}
                      </td>
                      <td className="px-3 sm:px-4 py-2 align-top">
                        <button
                          onClick={() => openEventModal(event)}
                          className="text-primary hover:text-primary/80 text-lg sm:text-xl"
                          title="Voir la fiche de l’événement"
                        >
                          <FaInfoCircle />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {selectedEvent && (
        <EventDetailsModal
          event={selectedEvent}
          isOpen={isEventModalOpen}
          onClose={closeEventModal}
          userRole={userRole}
        />
      )}

      {selectedObservations && (
        <ObservationsModal
          observations={selectedObservations}
          isOpen={isObservationsModalOpen}
          onClose={closeObservationsModal}
        />
      )}
    </div>
  );
};

export default ConvocationsPage;
